
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUserCategories } from '@/services/categories.service';
import { networkService } from '@/services/network.service';
import { offlineDb } from '@/db/offlineDb';
import OfflineLogger from '@/utils/offlineLogger';
import { UserCategory } from '@/types';
import { getValidUserId } from '@/utils/validation';

// ✅ FIX: Helper function per convertire SyncableUserCategory a UserCategory (non CategoryInfo)
const convertToUserCategory = (syncableCategory: any): UserCategory => ({
  id: syncableCategory.id,
  userId: syncableCategory.user_id || 'offline-user',
  name: syncableCategory.name || '',
  icon: syncableCategory.icon || '📦',
  color: syncableCategory.color || '#gray',
  textColor: syncableCategory.text_color || '#000000',
  position: syncableCategory.position || 0,
  // ✅ FIX: Utilizzare la proprietà corretta 'visibile' che esiste in UserCategory
  visibile: syncableCategory.visibile !== false
});

export function useUserCategories() {
  const [isOnline, setIsOnline] = useState(networkService.isOnline());
  
  // Update online status when network changes
  useEffect(() => {
    const handleNetworkChange = (status: { connected: boolean }) => {
      setIsOnline(status.connected);
    };
    
    const unsubscribe = networkService.addListener(handleNetworkChange);
    return () => unsubscribe();
  }, []);

  const { data: categories, isLoading, error, refetch } = useQuery({
    queryKey: ['user-categories'],
    queryFn: async () => {
      if (networkService.isOnline()) {
        try {
          const onlineCategories = await getUserCategories();
          
          await Promise.all(onlineCategories.map(async category => {
            try {
              await offlineDb.categories.put({
                id: category.id,
                user_id: category.userId || 'offline-user',
                name: category.name || '',
                icon: category.icon || '📦',
                color: category.color || '#gray',
                text_color: category.textColor || '#000000',
                position: category.position || 0,
                // ✅ FIX: Mappa correttamente la proprietà visibile
                visibile: category.visibile !== false,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                _syncStatus: 'synced' as const,
                _lastModified: Date.now()
              });
            } catch (err) {
              OfflineLogger.error('useUserCategories.cacheCategory', `Error caching category ${category.id}: ${err}`);
            }
          }));
          
          OfflineLogger.info('useUserCategories', `Cached ${onlineCategories.length} categories`);
          return onlineCategories;
        } catch (error) {
          OfflineLogger.error('useUserCategories.fetchOnline', error);
          // If online fetch fails, try to get from local DB as fallback
          const currentUserId = getValidUserId();
          if (!currentUserId) return [];
          const offlineCategories = await offlineDb.categories
            .where('user_id')
            .equals(currentUserId)
            .and(category => category.visibile === true)
            .and(category => category._syncStatus !== 'deleted')
            .toArray();
          return offlineCategories.map(convertToUserCategory);
        }
      } else {
        OfflineLogger.info('useUserCategories', 'Using offline categories from local DB');
        const currentUserId = getValidUserId();
        if (!currentUserId) return [];
        const offlineCategories = await offlineDb.categories
          .where('user_id')
          .equals(currentUserId)
          .and(category => category.visibile === true)
          .and(category => category._syncStatus !== 'deleted')
          .toArray();
        return offlineCategories.map(convertToUserCategory);
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    data: categories || [],
    isLoading,
    error,
    isOnline,
    refetch
  };
}
